// src/components/doctor/CampSubmissions.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Edit, Trash2, RefreshCw, Plus, Search, ChevronDown, ChevronUp, ClipboardList } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/use-toast";
import {
  listCampSubmissions,
  createCampSubmission,
  updateCampSubmission,
  deleteCampSubmission,
  listCampSubmissionLogs,
  getCampSubmissionLogsById,
} from "../../utils/campSubmissionsApi";

/* ------------------------------- Helpers -------------------------------- */

const ACTION_DB_TO_UI = { INSERT: "Added", UPDATE: "Edited", DELETE: "Deleted" };
const ACTION_UI_TO_DB = { Added: "INSERT", Edited: "UPDATE", Deleted: "DELETE" };

const INST_TYPE_OPTIONS = [
  { value: "Hospital", label: "Hospital" },
  { value: "Clinic", label: "Clinic" },
  { value: "School", label: "School" },
  { value: "College", label: "College" },
  { value: "NGO", label: "NGO" },
  { value: "Other", label: "Other" },
];

const ACTION_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Added", label: "Added" },
  { value: "Edited", label: "Edited" },
  { value: "Deleted", label: "Deleted" },
];

const cls = (...parts) => parts.filter(Boolean).join(" ");
const formatDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
const badge = (a) =>
  a === "INSERT"
    ? "bg-green-500/20 text-green-400 border border-green-500/30"
    : a === "UPDATE"
    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
    : a === "DELETE"
    ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
    : "bg-gray-500/20 text-gray-400 border border-gray-500/30";

/* ------------------------------- Form ----------------------------------- */

const SubmissionForm = ({
  initial = null,
  onSubmit,
  submitting = false,
  submitLabel = "Save",
  onCancel,
}) => {
  const [name, setName] = useState(initial?.name || "");
  const [dob, setDob] = useState(initial?.dob || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [institution, setInstitution] = useState(initial?.institution || "");
  const [institutionType, setInstitutionType] = useState(
    initial?.institution_type || initial?.institutionType || ""
  );
  const [comments, setComments] = useState(initial?.comments || "");
  const [err, setErr] = useState("");

  useEffect(() => {
    setName(initial?.name || "");
    setDob(initial?.dob || "");
    setEmail(initial?.email || "");
    setPhone(initial?.phone || "");
    setInstitution(initial?.institution || "");
    setInstitutionType(initial?.institution_type || initial?.institutionType || "");
    setComments(initial?.comments || "");
    setErr("");
  }, [initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    // Basic validation
    if (!name.trim()) return setErr("Name is required");
    if (email && !/^\S+@\S+\.\S+$/.test(email)) return setErr("Enter a valid email");

    // Always send BOTH snake_case and camelCase for maximum backend compatibility.
    // Also normalize empty strings to null where appropriate.
    const safeDob = dob || null;

    const payload = {
      // camelCase
      name: name.trim(),
      dob: safeDob,
      email: email || null,
      phone: phone || null,
      comments: comments || null,
      institution: institution || null,
      institutionType: institutionType || null,

      // snake_case mirrors
      institution_type: institutionType || null,
    };

    try {
      await onSubmit(payload);
      // Parent (create/edit modal caller) decides what to do after success
    } catch (e) {
      setErr(e?.message || "Failed to submit");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!!err && (
        <div className="p-3 rounded-lg border border-rose-500/50 bg-rose-500/10 text-rose-400 text-sm">
          {err}
        </div>
      )}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label htmlFor="name" className="text-white mb-2">Name *</Label>
          <Input
            id="name"
            className="bg-gray-800 border-gray-600 text-white"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            required
          />
        </div>
        <div>
          <Label htmlFor="dob" className="text-white mb-2">DOB</Label>
          <Input
            id="dob"
            type="date"
            className="bg-gray-800 border-gray-600 text-white"
            value={dob || ""}
            onChange={(e) => setDob(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-white mb-2">Email</Label>
          <Input
            id="email"
            type="email"
            className="bg-gray-800 border-gray-600 text-white"
            value={email || ""}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="text-white mb-2">Phone</Label>
          <Input
            id="phone"
            className="bg-gray-800 border-gray-600 text-white"
            value={phone || ""}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9876543210"
          />
        </div>
        <div>
          <Label htmlFor="institution" className="text-white mb-2">Institution</Label>
          <Input
            id="institution"
            className="bg-gray-800 border-gray-600 text-white"
            value={institution || ""}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="City Hospital"
          />
        </div>
        <div>
          <Label htmlFor="institutionType" className="text-white mb-2">Institution Type</Label>
          <select
            id="institutionType"
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={institutionType || ""}
            onChange={(e) => setInstitutionType(e.target.value || "")}
          >
            <option value="">Select type...</option>
            {INST_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="comments" className="text-white mb-2">Comments</Label>
          <textarea
            id="comments"
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={3}
            value={comments || ""}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Any notes..."
          />
        </div>
      </div>

      <div className="pt-2 flex gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className={cls(
            "flex-1 medical-gradient text-white hover:opacity-90",
            submitting && "opacity-50 cursor-not-allowed"
          )}
        >
          {submitting ? (
            <>
              <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              Saving...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              {submitLabel}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

/* ------------------------------- Modals ---------------------------------- */

const ConfirmDeleteModal = ({ open, onCancel, onConfirm, item, loading }) => {
  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="bg-gray-800 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Delete submission?</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-gray-300 space-y-2">
          <p>
            This will permanently delete{" "}
            <span className="font-medium text-white">{item?.name || "this record"}</span>.
          </p>
          <p>This action cannot be undone.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <Button
            variant="outline"
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin mr-2 h-4 w-4" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditSubmissionModal = ({ open, onClose, onSaved, item }) => {
  const [saving, setSaving] = useState(false);
  const onSubmit = async (payload) => {
    setSaving(true);
    try {
      await updateCampSubmission(item.id, payload);
      toast({
        title: "Success",
        description: "Camp submission updated successfully",
      });
      onSaved?.();
      onClose?.();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update submission",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Edit: {item?.name || ""}</DialogTitle>
        </DialogHeader>
        <SubmissionForm
          key={item?.id || "new"}
          initial={item}
          onSubmit={onSubmit}
          submitting={saving}
          submitLabel="Update"
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};

/* ----------------------------- Audit Panel ------------------------------- */

const AuditLogsPanel = ({ viewAsAdmin = false }) => {
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [actionOpt, setActionOpt] = useState(ACTION_OPTIONS[0]); // All
  const [submissionId, setSubmissionId] = useState(""); // ⬅️ optional filter by one submission

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const action =
        actionOpt?.value === "All" ? undefined : ACTION_UI_TO_DB[actionOpt.value];

      let r;
      if (submissionId.trim()) {
        // Per-submission logs
        r = await getCampSubmissionLogsById(submissionId.trim(), { 
          limit, 
          offset,
          view_as_admin: viewAsAdmin ? 'true' : undefined
        });
      } else {
        // All camp-submission logs
        r = await listCampSubmissionLogs({ 
          action, 
          limit, 
          offset,
          view_as_admin: viewAsAdmin ? 'true' : undefined
        });
      }
      const raw = Array.isArray(r?.items) ? r.items : [];
      setItems(raw);
    } catch (e) {
      setErr(e?.message || "Failed to load logs");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionOpt, limit, offset, submissionId]);

  useEffect(() => setOffset(0), [actionOpt, submissionId]);

  return (
    <div className="space-y-4">
      <Card className="glass-effect border-gray-700">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <Label className="text-white mb-2 block">Show</Label>
              <select
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3.5 py-2.5 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={actionOpt?.value || "All"}
                onChange={(e) => {
                  const selected = ACTION_OPTIONS.find(opt => opt.value === e.target.value) || ACTION_OPTIONS[0];
                  setActionOpt(selected);
                }}
              >
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <Label className="text-white mb-2 block">Submission ID (optional)</Label>
              <Input
                className="bg-gray-800 border-gray-600 text-white"
                placeholder="Filter by specific ID…"
                value={submissionId}
                onChange={(e) => setSubmissionId(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Page size</Label>
              <select
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3.5 py-2.5 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={limit}
                onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 25))}
              >
                {[10, 25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={load}
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                disabled={loading}
              >
                <RefreshCw className={loading ? "animate-spin mr-2 h-4 w-4" : "mr-2 h-4 w-4"} />
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {err && (
        <Card className="glass-effect border-rose-500/50">
          <CardContent className="p-4">
            <div className="text-rose-400 text-sm">{err}</div>
          </CardContent>
        </Card>
      )}
      {!err && loading && (
        <Card className="glass-effect border-gray-700">
          <CardContent className="p-8 text-center text-gray-400 flex flex-col items-center">
            <RefreshCw className="animate-spin text-2xl mb-2" />
            Loading logs…
          </CardContent>
        </Card>
      )}

      {!err && !loading && (
        <>
          {items.length === 0 ? (
            <Card className="glass-effect border-gray-700">
              <CardContent className="p-8 text-center text-gray-400">
                No events found.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {items.map((ev) => (
                <Card
                  key={`${ev.id}-${ev.happened_at}`}
                  className="glass-effect border-gray-700 hover:border-gray-600 transition-colors"
                >
                  <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between">
                    <div className="min-w-0 flex-1 mb-2 sm:mb-0">
                      <div className="flex items-center gap-2">
                        <span className={cls("px-2.5 py-1 rounded-full text-xs font-medium", badge(ev.action))}>
                          {ACTION_DB_TO_UI[ev.action] || ev.action}
                        </span>
                        <span className="text-sm text-gray-300 truncate">
                          Camp Submission{ev.row_id ? ` • ${String(ev.row_id).slice(0, 8)}` : ""}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 truncate">
                        By{" "}
                        <span className="font-medium text-gray-400">
                          {ev.actor_email || ev.actor_id || "Unknown"}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-400 self-end sm:self-auto">
                      {formatDateTime(ev.happened_at)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-between pt-4 gap-4">
            <div className="text-sm text-gray-400">Showing up to {limit} events</div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-700"
                disabled={loading || offset === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => setOffset((o) => o + limit)}
                className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-700"
                disabled={loading || items.length < limit}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ----------------------------- Mobile Table Row -------------------------- */

const MobileTableRow = ({ row, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card className="glass-effect border-gray-700 mb-3">
      <CardContent className="p-4">
        <div 
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium text-white truncate">{row.name}</div>
            <div className="text-sm text-gray-400 truncate">{row.email || "—"}</div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <Button
              variant="ghost"
              size="sm"
              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="p-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="text-gray-500">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </div>
        
        {expanded && (
          <div className="px-0 pb-0 pt-4 mt-4 border-t border-gray-700">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="text-gray-400">DOB:</div>
              <div className="text-gray-300">{row.dob || "—"}</div>
              
              <div className="text-gray-400">Phone:</div>
              <div className="text-gray-300">{row.phone || "—"}</div>
              
              <div className="text-gray-400">Institution:</div>
              <div className="text-gray-300">{row.institution || "—"}</div>
              
              <div className="text-gray-400">Type:</div>
              <div className="text-gray-300">{row.institution_type || row.institutionType || "—"}</div>
              
              <div className="text-gray-400">Added By:</div>
              <div className="text-gray-300">
                <span className="font-medium text-blue-400">{row.created_by_name || "—"}</span>
                {row.created_by_role && (
                  <span className="text-xs text-gray-500 ml-1">({row.created_by_role})</span>
                )}
              </div>
              
              <div className="text-gray-400">Created:</div>
              <div className="text-gray-300">{formatDateTime(row.created_at)}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ----------------------------- Main Page --------------------------------- */

const CampSubmissions = ({ initialTab = "form", viewAsAdmin = false }) => {
  const [tab, setTab] = useState(initialTab); // 'form' | 'list' | 'audit'
  const [creating, setCreating] = useState(false);

  // Update tab when initialTab prop changes
  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  // list state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [errList, setErrList] = useState("");

  // edit/delete modals
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingNow, setDeletingNow] = useState(false);

  const tabs = useMemo(
    () => [
      { key: "form", label: "Add / Edit" },
      { key: "list", label: "Submissions" },
      { key: "audit", label: "Audit Logs" },
    ],
    []
  );

  const loadList = async () => {
    setLoadingList(true);
    setErrList("");
    try {
      const res = await listCampSubmissions({
        q: q || undefined,
        limit,
        offset,
        sort: "created_at.desc",
        view_as_admin: viewAsAdmin ? 'true' : undefined,
      });
      setRows(res?.items || []);
      setTotal(Number.isFinite(res?.total) ? res.total : null);
    } catch (e) {
      setErrList(e?.message || "Failed to load submissions");
      setRows([]);
      setTotal(null);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (tab === "list") loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, limit, offset, viewAsAdmin]);

  const handleCreate = async (payload) => {
    setCreating(true);
    try {
      await createCampSubmission(payload);
      toast({
        title: "Success",
        description: "Camp submission created successfully",
      });
      // move to list and refresh
      setTab("list");
      setOffset(0);
      await loadList();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create submission",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row);
    setEditOpen(true);
  };

  const confirmDelete = (row) => {
    setDeleting(row);
    setDelOpen(true);
  };

  const doDelete = async () => {
    if (!deleting?.id) return;
    setDeletingNow(true);
    try {
      await deleteCampSubmission(deleting.id);
      toast({
        title: "Success",
        description: "Camp submission deleted successfully",
      });
      setDelOpen(false);
      setDeleting(null);
      await loadList();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete submission",
        variant: "destructive",
      });
    } finally {
      setDeletingNow(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="space-y-6"
    >
      {/* Tabs */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="h-5 w-5 text-blue-400" />
            <CardTitle className="text-white">Camp Submissions</CardTitle>
          </div>
          <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cls(
                  "flex-1 sm:flex-none px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0",
                  tab === t.key
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:text-white hover:bg-gray-700"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {tab === "form" && (
            <>
              <CardDescription className="text-gray-300 mb-4">
                Add a new camp submission entry
              </CardDescription>
              <SubmissionForm 
                onSubmit={handleCreate} 
                submitting={creating} 
                submitLabel="Create" 
              />
            </>
          )}

          {tab === "list" && (
            <>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between mb-5">
                <CardDescription className="text-gray-300">
                  View and manage all camp submissions
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={q}
                      onChange={(e) => {
                        setOffset(0);
                        setQ(e.target.value);
                      }}
                      placeholder="Search name/email/institution…"
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={limit}
                      onChange={(e) => {
                        setOffset(0);
                        setLimit(Math.max(1, Number(e.target.value) || 10));
                      }}
                      className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}/page
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="outline"
                      onClick={loadList}
                      disabled={loadingList}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      <RefreshCw className={loadingList ? "animate-spin mr-2 h-4 w-4" : "mr-2 h-4 w-4"} />
                      {loadingList ? "Refreshing…" : "Refresh"}
                    </Button>
                  </div>
                </div>
              </div>

              {errList && (
                <Card className="glass-effect border-rose-500/50 mb-4">
                  <CardContent className="p-4">
                    <div className="text-rose-400 text-sm">{errList}</div>
                  </CardContent>
                </Card>
              )}

              {/* Mobile View */}
              <div className="block sm:hidden">
                {loadingList ? (
                  <Card className="glass-effect border-gray-700">
                    <CardContent className="p-8 text-center text-gray-400">
                      <div className="flex justify-center items-center">
                        <RefreshCw className="animate-spin mr-2 h-5 w-5" />
                        Loading…
                      </div>
                    </CardContent>
                  </Card>
                ) : rows.length === 0 ? (
                  <Card className="glass-effect border-gray-700">
                    <CardContent className="p-8 text-center text-gray-400">
                      No records found.
                    </CardContent>
                  </Card>
                ) : (
                  <div>
                    {rows.map((r) => (
                      <MobileTableRow 
                        key={r.id} 
                        row={r} 
                        onEdit={startEdit}
                        onDelete={confirmDelete}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block overflow-auto border border-gray-700 rounded-xl">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">DOB</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Institution</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Added By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Created</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800/30 divide-y divide-gray-700">
                    {loadingList ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                          <div className="flex justify-center items-center">
                            <RefreshCw className="animate-spin mr-2 h-5 w-5" />
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-white max-w-xs truncate">{r.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{r.dob || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">{r.email || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{r.phone || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">{r.institution || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{r.institution_type || r.institutionType || "—"}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">
                            <span className="font-medium text-blue-400">{r.created_by_name || "—"}</span>
                            {r.created_by_role && (
                              <span className="text-xs text-gray-500 ml-1">({r.created_by_role})</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">{formatDateTime(r.created_at)}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                title="Edit"
                                onClick={() => startEdit(r)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                                title="Delete"
                                onClick={() => confirmDelete(r)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 text-sm gap-3">
                <div className="text-gray-400">
                  {Number.isFinite(total)
                    ? `Showing ${offset + 1} to ${Math.min(offset + limit, total)} of ${total}`
                    : `Showing ${rows.length} records`}
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-700"
                    disabled={loadingList || offset === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setOffset((o) => o + limit)}
                    className="flex-1 sm:flex-none border-gray-600 text-gray-300 hover:bg-gray-700"
                    disabled={loadingList || rows.length < limit}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}

            {tab === "audit" && <AuditLogsPanel viewAsAdmin={viewAsAdmin} />}
        </CardContent>
      </Card>

      {/* Modals */}
      <EditSubmissionModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={loadList}
        item={editing}
      />
      <ConfirmDeleteModal
        open={delOpen}
        onCancel={() => {
          setDelOpen(false);
          setDeleting(null);
        }}
        onConfirm={doDelete}
        item={deleting}
        loading={deletingNow}
      />
    </motion.div>
  );
};

export default CampSubmissions;
